import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Layout } from './components/shared';
import { ProtectedRoute, OnboardingGuard, SuspendedTenantGuard } from './components/auth';
import { useAuth } from './contexts/AuthContext';
import { setupAxiosInterceptors } from './utils/axiosInterceptor';
import { ToastProvider } from './hooks/useToast';
import { Loader2 } from 'lucide-react';

// Heavy route-level components loaded lazily so they don't inflate the initial bundle.
// @xyflow/react + Monaco editor together account for the majority of bundle weight.
const Home             = lazy(() => import('./components/management/Home').then(m => ({ default: m.Home })));
const Edit             = lazy(() => import('./components/workflows/edit/Edit').then(m => ({ default: m.Edit })));
const EditV2           = lazy(() => import('./components/workflows-v2').then(m => ({ default: m.EditV2 })));
const OAuthCallbackPage = lazy(() => import('./components/credentials/OAuthCallbackPage'));
const FormRenderer     = lazy(() => import('./components/public/FormRenderer').then(m => ({ default: m.FormRenderer })));

const RouteLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-zinc-900">
    <Loader2 className="h-10 w-10 animate-spin text-primary" />
  </div>
);

export default function App(): JSX.Element {
	const { keycloak, loading } = useAuth();

	useEffect(() => {
		if (keycloak) {
			setupAxiosInterceptors(keycloak);
		}
	}, [keycloak]);

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-zinc-900">
				<div className="text-center space-y-4">
					<Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
					<p className="text-muted-foreground">Initializing...</p>
				</div>
			</div>
		);
	}

	return (
		<ToastProvider>
			<Router basename="/">
				<OnboardingGuard>
					<SuspendedTenantGuard>
						<Suspense fallback={<RouteLoader />}>
						<Routes>
						{/* Protected Home route */}
						<Route 
							path="/" 
							element={
								<ProtectedRoute>
									<Home />
								</ProtectedRoute>
							} 
						/>
						
						{/* Protected routes */}
						<Route 
							path="/dashboard" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/workflows" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/logs" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/edit" 
							element={
								<ProtectedRoute>
									<Edit />
								</ProtectedRoute>
							} 
						/>
						
						{/* V2 Workflow Editor - n8n style */}
						<Route 
							path="/edit-v2" 
							element={
								<ProtectedRoute>
									<EditV2 />
								</ProtectedRoute>
							} 
						/>
						
						{/* Plugin routes - protected */}
						<Route 
							path="/plugins" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/plugins/:pluginId/settings" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						
						{/* Billing route - protected */}
						<Route 
							path="/billing" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						
						{/* Settings route - protected */}
						<Route 
							path="/settings" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>

						{/* Forms route - protected */}
						<Route 
							path="/forms" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>

						{/* Secrets route - protected */}
						<Route 
							path="/secrets" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>

						{/* Credentials route - protected */}
						<Route 
							path="/credentials" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>

						{/* Admin routes - protected (platform admin only) */}
						<Route 
							path="/admin/dashboard" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/tenants" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/tenants/:tenantId" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/users" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/credits" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/promo-codes" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/referral-codes" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/billing/parameters" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/billing/plans" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/billing/subscriptions" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/billing/invoices" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/plugins" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/templates" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/settings" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>
						<Route 
							path="/admin/email-audit-logs" 
							element={
								<ProtectedRoute>
									<Layout />
								</ProtectedRoute>
							} 
						/>

						{/* OAuth callback — lightweight popup relay page (EF-171) */}
						<Route
							path="/oauth/callback"
							element={<OAuthCallbackPage />}
						/>

						{/* Public Form Rendering Routes */}
						<Route 
							path="/f/:triggerId" 
							element={<FormRenderer />} 
						/>
						<Route 
							path="/f/test/:triggerId" 
							element={<FormRenderer />} 
						/>
						<Route 
							path="/f/p/:workspaceId/:formPath" 
							element={<FormRenderer />} 
						/>
						<Route 
							path="/f/preview/:previewToken" 
							element={<FormRenderer />} 
						/>
					</Routes>
					</Suspense>
				</SuspendedTenantGuard>
			</OnboardingGuard>

		</Router>
	</ToastProvider>
);
}
